import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { api } from "@/lib/api";
import { getRecaptchaToken } from "@/lib/recaptcha";
import { resizeImage } from "@/lib/image";
import ImageDrop from "@/components/ImageDrop";
import DocumentDrop from "@/components/DocumentDrop";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowRight, Globe2, ImageIcon, FileText, ScanLine, Search } from "lucide-react";
import { toast } from "sonner";

const LABELS = {
  fr: { title: "Mon site · Image / packaging · Document", foot: "Analyse gratuite — évaluation du risque, sans verdict de conformité." },
  en: { title: "My website · Image / packaging · Document", foot: "Free analysis — risk assessment, no compliance verdict." },
  de: { title: "Meine Website · Bild / Verpackung · Dokument", foot: "Kostenlose Analyse — Risikoeinschätzung, kein Konformitätsurteil." },
  es: { title: "Mi web · Imagen / envase · Documento", foot: "Análisis gratuito — evaluación del riesgo, sin veredicto de conformidad." },
  it: { title: "Il mio sito · Immagine / packaging · Documento", foot: "Analisi gratuita — valutazione del rischio, nessun verdetto di conformità." },
  nl: { title: "Mijn website · Afbeelding / verpakking · Document", foot: "Gratis analyse — risicobeoordeling, geen conformiteitsoordeel." },
};

/**
 * ScanBox — encadré de scan mis en évidence (site / image / document).
 * Liséré bleu + fond vert pâle dégradé vers le blanc.
 */
export default function ScanBox({ testidPrefix = "hero" }) {
  const { t, lang } = useLang();
  const labels = LABELS[lang] || LABELS.en;
  const nav = useNavigate();
  const [url, setUrl] = useState("");
  const [website, setWebsite] = useState("");
  const [loading, setLoading] = useState(false);
  const [imgFile, setImgFile] = useState(null);
  const [imgLoading, setImgLoading] = useState(false);
  const [docFile, setDocFile] = useState(null);
  const [docLoading, setDocLoading] = useState(false);

  const handleScanError = (err) => {
    if (err?.response?.status === 402) { nav("/limite"); return; }
    toast.error(err?.response?.status === 429 ? t("scan.ratelimit") : (err?.response?.data?.detail || t("scan.error")));
  };

  const done = (data) => {
    localStorage.setItem("empco_last_scan", JSON.stringify(data));
    nav(`/scan/${data.id}`);
  };

  const runFreeScan = async (e) => {
    e?.preventDefault?.();
    if (!url) return;
    setLoading(true);
    try {
      const recaptcha_token = await getRecaptchaToken("free_scan");
      const { data } = await api.post("/scan/free", { url, website, recaptcha_token });
      done(data);
    } catch (err) { handleScanError(err); } finally { setLoading(false); }
  };

  const runImageScan = async () => {
    if (!imgFile) return;
    setImgLoading(true);
    try {
      const recaptcha_token = await getRecaptchaToken("free_image_scan");
      const optimized = await resizeImage(imgFile);
      const fd = new FormData();
      fd.append("file", optimized);
      fd.append("website", website);
      if (recaptcha_token) fd.append("recaptcha_token", recaptcha_token);
      const { data } = await api.post("/scan/image/free", fd, { headers: { "Content-Type": "multipart/form-data" } });
      done(data);
    } catch (err) { handleScanError(err); } finally { setImgLoading(false); }
  };

  const runDocScan = async () => {
    if (!docFile) return;
    setDocLoading(true);
    try {
      const recaptcha_token = await getRecaptchaToken("free_document_scan");
      const fd = new FormData();
      fd.append("file", docFile);
      fd.append("website", website);
      if (recaptcha_token) fd.append("recaptcha_token", recaptcha_token);
      const { data } = await api.post("/scan/document/free", fd, { headers: { "Content-Type": "multipart/form-data" } });
      done(data);
    } catch (err) { handleScanError(err); } finally { setDocLoading(false); }
  };

  return (
    <div
      className="relative rounded-2xl border-2 border-primary bg-gradient-to-b from-[#E6F6EA] via-[#F4FBF6] to-white p-4 sm:p-6 shadow-[0_18px_50px_-18px_rgba(31,90,153,0.45)]"
      data-testid={`${testidPrefix}-scanner`}
    >
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white">
          <ScanLine className="w-3.5 h-3.5" /> {t("modes.eyebrow")}
        </span>
        <span className="text-sm font-semibold text-primary">{labels.title}</span>
      </div>

      <input type="text" name="website" value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" className="absolute opacity-0 h-0 w-0 pointer-events-none" aria-hidden="true" data-testid={`${testidPrefix}-honeypot`} />

      <Tabs defaultValue="site">
        <TabsList className="bg-white/80 backdrop-blur border border-primary/25 rounded-full p-1 h-auto gap-1 mb-4 flex flex-wrap">
          <TabsTrigger value="site" data-testid={`${testidPrefix}-tab-site`} className="rounded-full px-4 py-2 text-sm data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-none">
            <Globe2 className="w-4 h-4 mr-2" /> {t("tab.site")}
          </TabsTrigger>
          <TabsTrigger value="image" data-testid={`${testidPrefix}-tab-image`} className="rounded-full px-4 py-2 text-sm data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-none">
            <ImageIcon className="w-4 h-4 mr-2" /> {t("tab.image")}
          </TabsTrigger>
          <TabsTrigger value="document" data-testid={`${testidPrefix}-tab-document`} className="rounded-full px-4 py-2 text-sm data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-none">
            <FileText className="w-4 h-4 mr-2" /> {t("tab.document")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="site">
          <form onSubmit={runFreeScan} className="flex flex-col sm:flex-row gap-3 p-2 bg-white rounded-full border border-border shadow-[0_10px_30px_-12px_rgba(31,90,153,0.25)]" data-testid={`${testidPrefix}-scan-form`}>
            <div className="flex items-center flex-1 pl-4">
              <Search className="w-4 h-4 text-muted-foreground shrink-0" />
              <Input type="text" value={url} onChange={(e) => setUrl(e.target.value)} placeholder={t("scan.placeholder")}
                className="h-12 text-base border-0 shadow-none focus-visible:ring-0 bg-transparent" data-testid={`${testidPrefix}-scan-input`} />
            </div>
            <Button type="submit" disabled={loading || !url} className="h-12 rounded-full bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-light))] px-7 font-semibold" data-testid={`${testidPrefix}-scan-submit`}>
              {loading ? t("scan.loading") : (<><span>{t("hero.cta")}</span><ArrowRight className="w-4 h-4 ml-2" /></>)}
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="image">
          <div className="bg-white rounded-2xl border border-border p-4 shadow-[0_10px_30px_-12px_rgba(31,90,153,0.25)]">
            <ImageDrop onFile={setImgFile} file={imgFile} disabled={imgLoading} testid={`${testidPrefix}-image-drop`} hint={t("img.drop.hint")} />
            <Button onClick={runImageScan} disabled={imgLoading || !imgFile} className="mt-4 w-full h-12 rounded-lg bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-light))] font-semibold" data-testid={`${testidPrefix}-image-submit`}>
              <ScanLine className="w-4 h-4 mr-2" /> {imgLoading ? t("img.analyzing") : t("img.landing.cta")}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="document">
          <div className="bg-white rounded-2xl border border-border p-4 shadow-[0_10px_30px_-12px_rgba(31,90,153,0.25)]">
            <DocumentDrop onFile={setDocFile} file={docFile} disabled={docLoading} testid={`${testidPrefix}-document-drop`} hint={t("doc.drop.hint")} />
            <Button onClick={runDocScan} disabled={docLoading || !docFile} className="mt-4 w-full h-12 rounded-lg bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-light))] font-semibold" data-testid={`${testidPrefix}-document-submit`}>
              <ScanLine className="w-4 h-4 mr-2" /> {docLoading ? t("doc.analyzing") : t("doc.landing.cta")}
            </Button>
          </div>
        </TabsContent>
      </Tabs>
      <p className="mt-3 text-[11px] text-slate-500">{labels.foot}</p>
    </div>
  );
}
